import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  AddBloggleIntegrationReq,
  AddShopifyIntegrationReq,
  AddWordpressIntegrationReq,
  UpdateIntegrationReq,
} from '../../entity/api/routes/organization';
import {
  addBloggleIntegration,
  addShopifyIntegration,
  addWordpressIntegration,
  deleteOrganizationIntegration,
  getOrganizationIntegrations,
  getShopifyBlogs,
  getWordpressAuthors,
  getWordpressPluginDownloadUrl,
  updateOrganizationIntegration,
} from '../../sdk/organization-integration';
import { getQueryClient } from '../../sdk/request';

// Query Keys
export const organizationIntegrationsQueryKeys = {
  all: (organizationId: string) => ['organization-integrations', organizationId] as const,
} as const;

// Query: Get All Organization Integrations
export function useOrganizationIntegrations(organizationId: string) {
  return useQuery({
    queryKey: organizationIntegrationsQueryKeys.all(organizationId),
    queryFn: async function () {
      const response = await getOrganizationIntegrations(organizationId);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 2,
    enabled: organizationId.length > 0,
  });
}

// Helper: Prefetch organization integrations
export async function prefetchOrganizationIntegrations(organizationId: string): Promise<void> {
  const queryClient = getQueryClient();

  await queryClient.prefetchQuery({
    queryKey: organizationIntegrationsQueryKeys.all(organizationId),
    queryFn: async function () {
      const response = await getOrganizationIntegrations(organizationId);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

// Mutation: Add Shopify Integration
export function useAddShopifyIntegration() {
  const queryClient = getQueryClient();

  return useMutation({
    mutationFn: async function (params: {
      organizationId: string;
      data: AddShopifyIntegrationReq['body'];
    }) {
      return addShopifyIntegration(params.organizationId, params.data);
    },
    onSuccess: function (_data, variables) {
      void queryClient.invalidateQueries({
        queryKey: organizationIntegrationsQueryKeys.all(variables.organizationId),
      });
    },
  });
}

// Mutation: Delete Organization Integration
export function useDeleteOrganizationIntegration() {
  const queryClient = getQueryClient();

  return useMutation({
    mutationFn: async function (params: { organizationId: string; integrationId: string }) {
      return deleteOrganizationIntegration(params.organizationId, params.integrationId);
    },
    onSuccess: function (_data, variables) {
      void queryClient.invalidateQueries({
        queryKey: organizationIntegrationsQueryKeys.all(variables.organizationId),
      });
    },
  });
}

// Mutation: Update Organization Integration
export function useUpdateOrganizationIntegration() {
  const queryClient = getQueryClient();

  return useMutation({
    mutationFn: async function (params: {
      organizationId: string;
      integrationId: string;
      data: UpdateIntegrationReq['body'];
    }) {
      return updateOrganizationIntegration(
        params.organizationId,
        params.integrationId,
        params.data
      );
    },
    onSuccess: function (_data, variables) {
      void queryClient.invalidateQueries({
        queryKey: organizationIntegrationsQueryKeys.all(variables.organizationId),
      });
    },
  });
}

// Query: Get Shopify Blogs for an integration
export function useShopifyBlogs(organizationId: string, integrationId: string) {
  return useQuery({
    queryKey: ['shopify-blogs', organizationId, integrationId] as const,
    queryFn: async function () {
      return getShopifyBlogs(organizationId, integrationId);
    },
    enabled: organizationId.length > 0 && integrationId.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// Mutation: Add Bloggle Integration
export function useAddBloggleIntegration() {
  const queryClient = getQueryClient();

  return useMutation({
    mutationFn: async function (params: {
      organizationId: string;
      data: AddBloggleIntegrationReq['body'];
    }) {
      return addBloggleIntegration(params.organizationId, params.data);
    },
    onSettled: function (_data, _error, variables) {
      void queryClient.invalidateQueries({
        queryKey: organizationIntegrationsQueryKeys.all(variables.organizationId),
      });
    },
  });
}

// Query: Get WordPress authors for an integration
export function useWordpressAuthors(organizationId: string, integrationId: string) {
  return useQuery({
    queryKey: ['wordpress-authors', organizationId, integrationId] as const,
    queryFn: async function () {
      return getWordpressAuthors(organizationId, integrationId);
    },
    enabled: organizationId.length > 0 && integrationId.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// Mutation: Get WordPress plugin download URL (presigned, fetch on click)
export function useWordpressPluginDownloadUrl() {
  return useMutation({
    mutationFn: async function (organizationId: string) {
      return getWordpressPluginDownloadUrl(organizationId);
    },
  });
}

// Mutation: Add WordPress Integration
export function useAddWordpressIntegration() {
  const queryClient = getQueryClient();

  return useMutation({
    mutationFn: async function (params: {
      organizationId: string;
      data: AddWordpressIntegrationReq['body'];
    }) {
      return addWordpressIntegration(params.organizationId, params.data);
    },
    onSettled: function (_data, _error, variables) {
      void queryClient.invalidateQueries({
        queryKey: organizationIntegrationsQueryKeys.all(variables.organizationId),
      });
    },
  });
}
